import { useAppTheme } from "@/shared/lib/theme";
import { useTranslation } from "react-i18next";

const BRAND = "#22b17a";

function ModeMockup({ variant }: { variant: "light" | "dark" }) {
  const d = variant === "dark";
  const bg      = d ? "#0f172a" : "#f8fafc";
  const sidebar = d ? "#1e293b" : "#ffffff";
  const border  = d ? "#334155" : "#e2e8f0";
  const line    = d ? "#334155" : "#e2e8f0";
  const card    = d ? "#1e293b" : "#ffffff";
  const accent  = "#22b17a";

  return (
    <div
      className="rounded-lg overflow-hidden w-full mb-3 border"
      style={{ backgroundColor: bg, borderColor: border, height: 88 }}
    >
      <div className="flex h-full">
        {/* Sidebar */}
        <div
          className="flex flex-col gap-1.5 border-r"
          style={{ width: 36, padding: "10px 8px", backgroundColor: sidebar, borderColor: border }}
        >
          <div className="rounded-sm mb-1.5" style={{ width: 14, height: 14, backgroundColor: accent }} />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-sm" style={{ width: "100%", height: 5, backgroundColor: i === 0 ? `${accent}45` : line }} />
          ))}
        </div>

        {/* Content area */}
        <div className="flex flex-col gap-1.5 flex-1" style={{ padding: "8px 10px" }}>
          {/* Top bar */}
          <div className="flex items-center justify-between mb-0.5">
            <div className="rounded" style={{ width: 44, height: 5, backgroundColor: line }} />
            <div className="rounded-full" style={{ width: 14, height: 14, backgroundColor: accent }} />
          </div>
          {/* Stat cards */}
          <div className="flex gap-1.5 flex-1">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="flex-1 rounded border flex flex-col justify-center"
                style={{ padding: "6px 8px", backgroundColor: card, borderColor: border }}
              >
                <div className="rounded mb-1" style={{ width: 20, height: 4, backgroundColor: accent, opacity: 0.75 }} />
                <div className="rounded" style={{ width: 28, height: 6, backgroundColor: line }} />
              </div>
            ))}
          </div>
          {/* List rows */}
          <div className="rounded border" style={{ padding: "6px 8px", backgroundColor: card, borderColor: border }}>
            {[0, 1].map((i) => (
              <div key={i} className="flex gap-2 items-center" style={{ marginBottom: i === 0 ? 5 : 0 }}>
                <div className="rounded" style={{ width: 10, height: 4, backgroundColor: line }} />
                <div className="rounded flex-1" style={{ height: 4, backgroundColor: line }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const SunIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-400">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
    <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function ThemeSelector() {
  const { t } = useTranslation();
  const { theme, setTheme } = useAppTheme();

  return (
    <div className="space-y-8">
      {/* ── Mode Selection ── */}
      <div>
        <p className="text-sm font-semibold text-primary mb-0.5">{t("common.src.themeSelectorThemeSelector.displayMode")}</p>
        <p className="text-xs text-tertiary mb-4">
          {t("common.src.themeSelectorThemeSelector.chooseHowThe")}</p>

        <div className="grid grid-cols-2 gap-3">
          {(["light", "dark"] as const).map((mode) => {
            const active = theme === mode;
            return (
              <button
                key={mode}
                onClick={() => setTheme(mode)}
                aria-pressed={active}
                className={[
                  "relative rounded-xl p-4 text-left border-2 transition-all duration-200 cursor-pointer",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  active
                    ? "shadow-md"
                    : "border hover:border-strong hover:shadow-sm",
                ].join(" ")}
                style={active ? { borderColor: BRAND } : {}}
              >
                {active && (
                  <span
                    className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: BRAND }}
                  >
                    <CheckIcon />
                  </span>
                )}

                <ModeMockup variant={mode} />

                <div className="flex items-center gap-2">
                  {mode === "light" ? <SunIcon /> : <MoonIcon />}
                  <span className="text-sm font-medium text-primary capitalize">
                    {mode}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
